import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Skeleton } from '@/nova/components';
import {
  Card,
  StatsCard,
  Stats,
  Stat,
  StatNum,
  StatLabel,
  ManageSlot,
  ManageProbe,
} from './AffiliateSummaryCard.styles';

export type AffiliateSummaryCardProps = {
  affiliateCount?: number;
  campaignCount?: number;
  loading: boolean;
  /** Opens the external affiliate console (SSO) in a new tab. */
  onManage: () => void;
};

/** Whether the full console label fits its button on one line, read off the hidden full-label twin (see
 *  ManageProbe). Re-checks when the card resizes, when the label changes (a language switch) and once web fonts
 *  have loaded (a fallback font measures differently). Runs before paint, so the wrong label never flashes. */
const useFullLabelFits = (probeRef: React.RefObject<HTMLDivElement>, label: string): boolean => {
  const [fits, setFits] = React.useState(true);
  React.useLayoutEffect(() => {
    const probe = probeRef.current;
    const button = probe?.querySelector('button');
    if (!probe || !button) return undefined;
    let live = true;
    // Compare against the button's CONTENT box in unrounded px. `scrollWidth` can't be used: the label is centred, so
    // it spills into the padding first (not counted as overflow) and the integer rounding hides the rest, which let
    // the real button wrap while the check still said "fits".
    const measure = (): void => {
      if (!live) return;
      const cs = getComputedStyle(button);
      const px = (value: string): number => parseFloat(value) || 0;
      const room =
        button.getBoundingClientRect().width -
        px(cs.borderLeftWidth) -
        px(cs.borderRightWidth) -
        px(cs.paddingLeft) -
        px(cs.paddingRight);
      // What one line needs: the icon plus the full label at its natural (nowrap) width, and the gap between them.
      const items = Array.from(button.children);
      const need =
        items.reduce((sum, el) => sum + el.getBoundingClientRect().width, 0) +
        px(cs.columnGap) * Math.max(items.length - 1, 0);
      setFits(need <= room + 0.01);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(probe);
    void document.fonts?.ready.then(measure);
    return () => {
      live = false;
      observer.disconnect();
    };
  }, [probeRef, label]);
  return fits;
};

/**
 * The minimal-MVP affiliate summary: how many affiliates + campaigns, and the
 * "Advanced Affiliates Dashboard" button that SSOs into the external console (shortened to "Advanced dashboard"
 * when the full label would not fit on one line). Sits beside the
 * this-month overview KPIs and is always shown (the deeper tabbed management is what the
 * `affiliate-management` flag gates).
 */
export const AffiliateSummaryCard = ({
  affiliateCount,
  campaignCount,
  loading,
  onManage,
}: AffiliateSummaryCardProps): React.ReactElement => {
  const { t } = useTranslation();
  const value = (n: number | undefined): string => (typeof n === 'number' ? String(n) : '–');
  const fullLabel = t('Advanced Affiliates Dashboard');
  const probeRef = React.useRef<HTMLDivElement>(null);
  const fullLabelFits = useFullLabelFits(probeRef, fullLabel);

  return (
    <Card>
      <StatsCard>
        <Stats>
          <Stat>
            {loading ? <Skeleton width={52} height={40} radius={8} /> : <StatNum text={value(affiliateCount)} tag="span" />}
            <StatLabel text={t('Affiliates')} tag="span" />
          </Stat>
          <Stat>
            {loading ? <Skeleton width={52} height={40} radius={8} /> : <StatNum text={value(campaignCount)} tag="span" />}
            <StatLabel text={t('Campaigns')} tag="span" />
          </Stat>
        </Stats>
        <ManageSlot>
          <ManageProbe ref={probeRef} aria-hidden="true">
            <Button label={fullLabel} icon="external-link" variant="outline" fullWidth />
          </ManageProbe>
          <Button
            label={fullLabelFits ? fullLabel : t('Advanced dashboard')}
            icon="external-link"
            variant="outline"
            fullWidth
            onClick={onManage}
          />
        </ManageSlot>
      </StatsCard>
    </Card>
  );
};

export default AffiliateSummaryCard;
